import { useRef, useState, useEffect } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { Avatar, Box, Button, Divider, Hidden, List, ListItem, ListItemText, Popover, Typography } from '@mui/material';
import InboxTwoToneIcon from '@mui/icons-material/InboxTwoTone';
import { lighten, styled } from '@mui/material/styles';
import ExpandMoreTwoToneIcon from '@mui/icons-material/ExpandMoreTwoTone';
import AccountBoxTwoToneIcon from '@mui/icons-material/AccountBoxTwoTone';
import LockOpenTwoToneIcon from '@mui/icons-material/LockOpenTwoTone';
import AccountTreeTwoToneIcon from '@mui/icons-material/AccountTreeTwoTone';
import { IEmployeeProfile } from 'src/Common/Interfaces/Profile/IEmployeeProfile';
import { notifyAuthTokenChanged } from 'src/Common/settings/deviceIdentity';
import { clearImageUrlCache } from 'src/Common/utils/imageUtils';

const UserBoxButton = styled(Button)(({ theme }) => `
        padding-left: ${theme.spacing(1)};
        padding-right: ${theme.spacing(1)};
`);

const MenuUserBox = styled(Box)(({ theme }) => `
        background: ${theme.colors.alpha.black[5]};
        padding: ${theme.spacing(2)};
`);

const UserBoxText = styled(Box)(({ theme }) => `
        text-align: left;
        padding-left: ${theme.spacing(1)};
`);

const UserBoxLabel = styled(Typography)(({ theme }) => `
        font-weight: ${theme.typography.fontWeightBold};
        color: ${theme.palette.secondary.main};
        display: block;
`);

const UserBoxDescription = styled(Typography)(({ theme }) => `
        color: ${lighten(theme.palette.secondary.main, 0.5)};
`);

function HeaderUserbox() {
  const ref = useRef<any>(null);
  const [isOpen, setOpen] = useState<boolean>(false);
  const navigate = useNavigate();
  
  // Retrieve profile data from sessionStorage
  const profileData = JSON.parse(sessionStorage.getItem('profileData') || '[]');
  const employeeProfile = JSON.parse(localStorage.getItem('employeeProfile') || '{}') as IEmployeeProfile;
  // Check if profileData is an array and has at least one item
  const user = profileData.length > 0 ? profileData[0] : {};

  const userProfile = {
    name: employeeProfile.FirstName || '',
    avatar: user.Photo || '/static/images/avatars/1.jpg',
    jobtitle: employeeProfile.Designation || ''
  };
  const storeType = localStorage.getItem('storeType') || '';

  const handleOpen = (): void => {
    setOpen(true);
  };

  const handleClose = (): void => {
    setOpen(false);
  };

  const handleSignOut = (): void => {
    // Clear sessionStorage and localStorage
    sessionStorage.removeItem('authData');
    sessionStorage.removeItem('profileData');
    localStorage.removeItem('authToken');
    localStorage.removeItem('userRoles');
    localStorage.removeItem('employeeProfile');
    localStorage.removeItem('restrictToBilling');
    // Same-tab sign-out: the hub connection and the device session stop (the ledger stays).
    notifyAuthTokenChanged();
    clearImageUrlCache();

    // Redirect to the login page
    navigate('/login');
  };


  return (
    <>
      <UserBoxButton color="secondary" ref={ref} onClick={handleOpen}>
        {/* <Avatar variant="rounded" alt={userProfile.name} src={userProfile.avatar} /> */}

        <Avatar
          variant="rounded"
          alt={userProfile.name}
          src={          
            `https://ui-avatars.com/api/?name=${encodeURIComponent(userProfile.name?.charAt(0))}&background=random&size=100`
          }
        >
          {!userProfile.avatar && userProfile.name?.charAt(0).toUpperCase()}
        </Avatar>


        <Hidden mdDown>
          <UserBoxText>
            <UserBoxLabel variant="body1">{userProfile.name}</UserBoxLabel>
            <UserBoxDescription variant="body2">{userProfile.jobtitle}</UserBoxDescription>
          </UserBoxText>
        </Hidden>
        <Hidden smDown>
          <ExpandMoreTwoToneIcon sx={{ ml: 1 }} />
        </Hidden>
      </UserBoxButton>
      <Popover
        anchorEl={ref.current}
        onClose={handleClose}
        open={isOpen}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right'
        }}
      >
        <MenuUserBox sx={{ minWidth: 210 }} display="flex">
          {/* <Avatar variant="rounded" alt={userProfile.name} src={userProfile.avatar} /> */}
          <Avatar
            variant="rounded"
            alt={userProfile.name}
            src={             
              `https://ui-avatars.com/api/?name=${encodeURIComponent(userProfile.name?.charAt(0))}&background=random&size=100`
            }
          >
            {!userProfile.avatar && userProfile.name?.charAt(0).toUpperCase()}
          </Avatar>


          <UserBoxText>
            <UserBoxLabel variant="body1">{userProfile.name}</UserBoxLabel>
            <UserBoxDescription variant="body2">{userProfile.jobtitle}</UserBoxDescription>
            <UserBoxDescription variant="body2">{employeeProfile.branchName}</UserBoxDescription>
            {storeType && (
              <UserBoxDescription variant="body2">Store Type: {storeType}</UserBoxDescription>
            )}
          </UserBoxText>
        </MenuUserBox>
        <Divider sx={{ mb: 0 }} />
        <List sx={{ p: 1 }} component="nav">
          <ListItem button to="/management/profile/details" component={NavLink}>
            <AccountBoxTwoToneIcon fontSize="small" />
            <ListItemText primary="My Profile" />
          </ListItem>
          <ListItem button to="/dashboards/messenger" component={NavLink}>
            <InboxTwoToneIcon fontSize="small" />
            <ListItemText primary="Messenger" />
          </ListItem>
          <ListItem button to="/management/profile/settings" component={NavLink}>
            <AccountTreeTwoToneIcon fontSize="small" />
            <ListItemText primary="Account Settings" />
          </ListItem>
        </List>
        <Divider />
        <Box sx={{ m: 1 }}>
          <Button color="primary" fullWidth onClick={handleSignOut}>
            <LockOpenTwoToneIcon sx={{ mr: 1 }} />
            Sign out
          </Button>
        </Box>
      </Popover>
    </>
  );
}

export default HeaderUserbox;
